import * as yup from "yup";
import {
  FilterDefinition,
  AppliedFilter,
  FilterVariantDefinition,
} from "components/Shared/FilterEditor/utils";

function configureValueSchema(schema: yup.MixedSchema) {
  return (
    schema
      .label("Value")
      // eslint-disable-next-line no-template-curly-in-string
      .required("Please enter a filter value")
      .transform((v: unknown, o: unknown, s: yup.MixedSchema) => {
        const value = v ?? o;
        const isArrayValue = Array.isArray(value);
        const isArraySchema = s instanceof yup.ArraySchema;
        if (isArrayValue && !isArraySchema) {
          return (value as unknown[])[0];
        }
        if (!isArrayValue && isArraySchema) {
          return value ? [value] : undefined;
        }
        return value;
      })
  );
}

export function getSchemaForDef(
  filterDefinition: FilterDefinition,
  filterVariantDefinition: FilterVariantDefinition,
  isConfiguredAsValue = true,
): yup.MixedSchema | undefined {
  const schema = filterVariantDefinition.schema || filterDefinition.schema;
  if (!schema) return;
  if (!isConfiguredAsValue) return schema as yup.MixedSchema;
  return configureValueSchema(schema as yup.MixedSchema);
}

export function getAppliedFilterSchema(filterDefinitions: FilterDefinition[]) {
  const filterIds: string[] = [];
  const variantIdsByFilterId: {
    [filterId: string]: string[];
  } = {};
  const valueSchemaByVariantIdByFilterId: {
    [filterId: string]: {
      [variantId: string]: yup.MixedSchema | undefined;
    };
  } = {};

  filterDefinitions.forEach((fd) => {
    filterIds.push(fd.id);
    valueSchemaByVariantIdByFilterId[fd.id] = {};
    variantIdsByFilterId[fd.id] = fd.variants.reduce<string[]>((acc, fdv) => {
      if (fdv.schema || fd.schema) {
        valueSchemaByVariantIdByFilterId[fd.id][fdv.id] = getSchemaForDef(
          fd,
          fdv,
        );
      }
      acc.push(fdv.id);
      return acc;
    }, []);
  });

  const schema: yup.ObjectSchema<AppliedFilter> = yup.object().shape({
    filter: yup
      .string()
      .label("Filter")
      .oneOf(filterIds, "Please select a valid filter")
      // eslint-disable-next-line no-template-curly-in-string
      .required("${label} must be provided"),
    variant: yup
      .string()
      .label("Variant")
      .when("filter", ([filter], s) => {
        if (!filter || typeof filter !== "string") return s;
        const variantIds = variantIdsByFilterId[filter] || {};
        return s.oneOf(
          variantIds || [],
          "Please select a valid filter type for this filter",
        );
      })
      // eslint-disable-next-line no-template-curly-in-string
      .required("${label} must be provided"),
    value: configureValueSchema(
      yup
        .mixed()
        .defined()
        .required()
        .when(["filter", "variant"], ([filter, variant], s) => {
          if (
            !filter ||
            !variant ||
            typeof filter !== "string" ||
            typeof variant !== "string"
          )
            return s;
          const valueSchema =
            valueSchemaByVariantIdByFilterId[filter]?.[variant];
          if (valueSchema) return valueSchema;
          return s;
        }),
    ),
  });
  return schema;
}

export function getAppliedFiltersSchema(filterDefinitions: FilterDefinition[]) {
  return yup
    .array()
    .of<AppliedFilter>(getAppliedFilterSchema(filterDefinitions))
    .defined()
    .ensure()
    .default([] as AppliedFilter[]);
}

export function getAppliedFilterSchemaForFieldName(
  fieldName: string,
  filterDefinitions: FilterDefinition[],
) {
  return yup
    .object()
    .shape({ [fieldName]: getAppliedFiltersSchema(filterDefinitions) });
}
